import dynamic from 'next/dynamic';
import { useCallback } from 'react';
import env from '@/config/environment';
import { LockScrollGlobally } from '@/presentation/styles/global';
import { useAuthModal } from '../../components/Authentication/hooks/useAuthModal';
import {
  AuthKind,
  LoginComponentSourceName,
} from '../../components/Authentication/types';
import { useTranslation } from '../useTranslation';

import { BannerImage, ModalTitleWrapper } from './style';

const AuthModal = dynamic(
  () =>
    import('@/presentation/components/Authentication').then(
      (mod) => mod.default
    ),
  { ssr: false }
);

const ModalTitle = ({ cashbackPercentage = 0 }) => {
  const { t } = useTranslation('login');

  return (
    <ModalTitleWrapper>
      {t('cashback-auth-guard-modal-title')}{' '}
      <strong>
        {cashbackPercentage}% {t('cashback-auth-guard-modal-title-cashback')}
      </strong>
    </ModalTitleWrapper>
  );
};

type ShowCashbackAuthGuardModalParams = {
  cashbackPercentage: number;
  retry?: () => void;
};

export function useCashbackAuthGuardModal() {
  const { t } = useTranslation('login');
  const { showModal, closeModal } = useAuthModal();

  const googleClientId = env.AUTH_GOOGLE_APP_ID;

  const modalSubtitle = t('cashback-auth-guard-modal-subtitle');

  const showAuthModal = useCallback(
    ({
      cashbackPercentage,
      retry = () => {},
    }: ShowCashbackAuthGuardModalParams) => {
      showModal(
        <>
          <AuthModal
            kind={AuthKind.LOGIN}
            modalTitle={<ModalTitle cashbackPercentage={cashbackPercentage} />}
            modalSubtitle={modalSubtitle}
            componentSourceName={LoginComponentSourceName.cashbackAuthGuard}
            onFinishSuccessfully={retry}
            onCloseModal={closeModal}
            googleClientId={googleClientId}
          />
          <LockScrollGlobally lock />
        </>,
        {
          bannerImage: (
            <BannerImage
              $mobileBannerImage="/assets/super-cashback/login/cashback-banner.sm.webp"
              $desktopBannerImage="/assets/super-cashback/login/cashback-banner.webp"
            />
          ),
        }
      );
    },
    [showModal, modalSubtitle, closeModal, googleClientId]
  );

  return {
    showAuthModal,
  };
}
